import { createContext, useContext, useEffect, useState } from "react"
import { CLIENT_CONFIG } from "@/constants"
import { useClientTheme } from "@/hooks/use-client-theme"
import {
  applyAppThemeMode,
  readStoredAppTheme,
  resolveAppThemeToMode,
  type AppTheme,
} from "@/lib/app-theme"

type Theme = AppTheme

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  /** When false, skip applying light/dark on `<html>` (guest/marketing surfaces own the class). */
  manageDocumentTheme?: boolean
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  manageDocumentTheme = true,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() =>
    readStoredAppTheme(storageKey, defaultTheme)
  )

  // Apply client-specific theme CSS variables
  useClientTheme()

  // Sync `tenant-*` on <html> so index.css tenant rules (e.g. .dark.tenant-talentco) apply.
  useEffect(() => {
    const root = window.document.documentElement
    for (const cls of [...root.classList]) {
      if (cls.startsWith("tenant-")) root.classList.remove(cls)
    }
    root.classList.add(`tenant-${CLIENT_CONFIG}`)
    return () => {
      root.classList.remove(`tenant-${CLIENT_CONFIG}`)
    }
  }, [])

  useEffect(() => {
    if (!manageDocumentTheme) return

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)")
      const sync = () => applyAppThemeMode(resolveAppThemeToMode("system"))
      sync()
      mq.addEventListener("change", sync)
      return () => mq.removeEventListener("change", sync)
    }

    applyAppThemeMode(theme)
  }, [manageDocumentTheme, theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
